import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/EditOutlined";
import type { SchemeContent, SchemeStation } from "@shared/types/foyer";
import { orderCatalogue } from "@shared/catalogue-order";
import { certificationOf } from "@shared/app-certification";
import { resolveLocalized } from "@shared/i18n";
import {
  foyerApi,
  type AppRecord,
  type RadioStationRecord,
} from "../../http/foyer";
import { CustomStationDialog } from "../template/ContentEditor";
import { tokens } from "../../theme";
import CatalogueList from "./CatalogueList";
import { PanelHeading } from "./StartNavLayout";

interface PaneProps {
  content: SchemeContent;
  supportedLocales: string[];
  defaultLocale: string;
  onChange: (next: SchemeContent) => void;
}

/** The pinned row's line, in place of its tag (design: "Ligger först så länge
 *  appen visas"). */
const PINNED_NOTE = "Stays first for as long as the app is shown.";

/**
 * Start & navigation → Apps (NendaPortal_1 1677:13245). Same model as before:
 * the global catalogue, switched per place (`disabledAppIds`) and ordered
 * (`appOrder`); a certified app that is on is pinned first (NFLX-4) and a care
 * place is never offered one (NFLX-6).
 */
export function AppsPanel({
  content,
  global,
  isCare,
  onChange,
}: PaneProps & { global: AppRecord[] | null; isCare: boolean }) {
  const disabled = content.disabledAppIds ?? [];
  const isOn = (id: string) => !disabled.includes(id);
  const toggle = (id: string) =>
    onChange({
      ...content,
      disabledAppIds: isOn(id)
        ? [...disabled, id]
        : disabled.filter((x) => x !== id),
    });

  const pinnedBy = useMemo(() => {
    const m = new Map<string, string>();
    if (isCare) return m;
    for (const a of global ?? []) {
      if (certificationOf(a) && !disabled.includes(a.id))
        m.set(a.id, PINNED_NOTE);
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [global, content.disabledAppIds, isCare]);

  const offerable = useMemo(
    () =>
      isCare
        ? (global ?? []).filter((a) => certificationOf(a) === null)
        : (global ?? []),
    [global, isCare]
  );
  const ordered = useMemo(() => {
    const list = orderCatalogue(offerable, content.appOrder);
    return [
      ...list.filter((a) => pinnedBy.has(a.id)),
      ...list.filter((a) => !pinnedBy.has(a.id)),
    ];
  }, [offerable, content.appOrder, pinnedBy]);

  return (
    <Box data-testid="apps-panel">
      <PanelHeading
        title={`Apps (${ordered.length})`}
        subtitle="Choose which apps are shown and drag to change the order."
      />
      {global === null ? (
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      ) : ordered.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No apps in the catalogue yet.
        </Typography>
      ) : (
        <CatalogueList
          data-testid="apps-list"
          items={ordered}
          isOn={isOn}
          onToggle={toggle}
          pinnedBy={pinnedBy}
          onReorder={(appOrder) => onChange({ ...content, appOrder })}
        />
      )}
    </Box>
  );
}

/**
 * Start & navigation → Radio (NendaPortal_1 1694:14864): the global channels
 * switched and ordered per place (`disabledStationIds`, `stationOrder`), then
 * this place's own channels (`stations`), added and edited in a dialog.
 */
export function RadioPanel({
  content,
  supportedLocales,
  defaultLocale,
  onChange,
}: PaneProps) {
  const [global, setGlobal] = useState<RadioStationRecord[] | null>(null);
  useEffect(() => {
    let alive = true;
    foyerApi
      .listRadioStations()
      .then((list) => alive && setGlobal(Array.isArray(list) ? list : []))
      .catch(() => alive && setGlobal([]));
    return () => {
      alive = false;
    };
  }, []);

  const disabled = content.disabledStationIds ?? [];
  const isOn = (id: string) => !disabled.includes(id);
  const toggle = (id: string) =>
    onChange({
      ...content,
      disabledStationIds: isOn(id)
        ? [...disabled, id]
        : disabled.filter((x) => x !== id),
    });
  const ordered = useMemo(
    () => orderCatalogue(global ?? [], content.stationOrder),
    [global, content.stationOrder]
  );

  // This place's own channels. Always shown - there is no switch for them;
  // removing one is how it stops showing.
  const stations = content.stations ?? [];
  const setStations = (s: SchemeStation[]) =>
    onChange({ ...content, stations: s });
  const [dialogIdx, setDialogIdx] = useState<number | "new" | null>(null);

  return (
    <Box data-testid="radio-panel">
      <PanelHeading
        title={`Radio channels (${ordered.length})`}
        subtitle="Choose which channels are shown and drag to change the order."
      />
      {global === null ? (
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      ) : ordered.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No channels in the catalogue yet.
        </Typography>
      ) : (
        <CatalogueList
          data-testid="stations-list"
          items={ordered}
          isOn={isOn}
          onToggle={toggle}
          onReorder={(stationOrder) => onChange({ ...content, stationOrder })}
        />
      )}

      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        sx={{ mt: 5 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <PanelHeading
            title="Custom channels"
            subtitle="Create radio channels of your own for this place."
          />
        </Box>
        <Button
          variant="outlined"
          endIcon={<AddIcon />}
          onClick={() => setDialogIdx("new")}
          sx={{ flexShrink: 0, textTransform: "none" }}
          data-testid="radio-add-custom"
        >
          Add custom channel
        </Button>
      </Stack>
      {stations.length > 0 && (
        <Box sx={{ borderTop: "1px solid", borderColor: tokens.divider }}>
          {stations.map((s, i) => {
            const tag = resolveLocalized(s.tag, defaultLocale, defaultLocale);
            return (
              <Stack
                key={s.id}
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{
                  minHeight: 64,
                  py: 1,
                  borderBottom: "1px solid",
                  borderColor: tokens.divider,
                }}
                data-testid={`custom-station-${s.id}`}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 40,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {s.logoUrl && (
                    <Box
                      component="img"
                      src={s.logoUrl}
                      alt=""
                      sx={{ maxWidth: "100%", maxHeight: "100%" }}
                    />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>
                    {s.name}
                  </Typography>
                  {tag && (
                    <Typography
                      sx={{ fontSize: 13, color: tokens.inkMuted }}
                      noWrap
                    >
                      {tag}
                    </Typography>
                  )}
                </Box>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => setDialogIdx(i)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    aria-label={`Delete ${s.name}`}
                    onClick={() =>
                      setStations(stations.filter((_, idx) => idx !== i))
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            );
          })}
        </Box>
      )}

      {dialogIdx !== null && (
        <CustomStationDialog
          initial={dialogIdx === "new" ? null : stations[dialogIdx]}
          supportedLocales={supportedLocales}
          defaultLocale={defaultLocale}
          onClose={() => setDialogIdx(null)}
          onSave={(st) => {
            setStations(
              dialogIdx === "new"
                ? [...stations, st]
                : stations.map((x, idx) => (idx === dialogIdx ? st : x))
            );
            setDialogIdx(null);
          }}
        />
      )}
    </Box>
  );
}
